import { defineStore } from 'pinia'
import { NewsletterPopupModel } from '@/models/NewsletterPopupTypes'
import { ref } from 'vue'

export const useNewsletterStore = defineStore('newsletterStore', () => {
    const newsletterController = 'api/NewsletterPopup'
    const nameRegEx = new RegExp(/^[\p{L}\s-]+$/u)
    const emailRegEx = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i)
    const phoneNumberRegEx = new RegExp(/^(?:\+\d{1,3}\s?)?(?:\d{10})$/)

    const newsletterData = ref<Array<string>>([])
    const locallizations = ref<{ [key: string]: string }>({})

    async function fetchNewsletter() {
        const currentCulture =
            document.documentElement.dataset.culture?.toString() ?? "";
        const url =
            newsletterController +
            "?" +
            new URLSearchParams({
                culture: currentCulture,
            });
        const resp = await fetch(url, {
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                culture: currentCulture,
            },
        });
        const data = (await resp.json()) as NewsletterPopupModel

        locallizations.value = data.Translations
    }

    async function postNewsletter(
        firstName: string,
        lastName: string,
        email: string,
        phoneNumber: string,
        terms: boolean,
        turnstileToken: string
    ) {
        const model = {
            FirstName: firstName,
            LastName: lastName,
            Email: email,
            PhoneNumber: phoneNumber,
            Terms: terms,
            // Canonical Turnstile field name; the server validates it before the handler runs.
            'cf-turnstile-response': turnstileToken
        }

        try {
            const resp = await fetch(newsletterController, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json;charset=utf-8',
                    culture: document.documentElement.dataset.culture?.toString() ?? '',
                },
                body: JSON.stringify(model),
            })

            return resp.status == 200
        } catch (error) {
            // A network-level failure rejects rather than resolving. Uncaught it would escape the
            // caller's click handler, leaving the user with no error message and no thank-you - the
            // submit button would simply appear to do nothing.
            console.warn('Newsletter submission request failed', error)
            return false
        }
    }

    function getLocalization(key: string) {
        let res = ''

        try {
            res = locallizations.value[key]
        } catch (error) {
            console.warn(`No localization for ${key}`)
        }

        return res
    }

    function isNameValid(name: string) {
        return nameRegEx.test(name);
    }

    function isEmailValid (email: string){
        return emailRegEx.test(email);
    }

    function isPhoneNumberValid(email: string) {
        return phoneNumberRegEx.test(email);
    }

    return {
        newsletterData,
        fetchNewsletter,
        postNewsletter,
        getLocalization,
        isNameValid,
        isEmailValid,
        isPhoneNumberValid
    };
})
